import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject, timer } from 'rxjs';
import { catchError, finalize, map, retry, switchMap, take, takeUntil } from 'rxjs/operators';
import { ChatTwilioApiClient } from '@nimbus-global/features/chat/services/twilio/chat-twilio.api-client';
import { AuthorType, IChat, IChatCallerInfo, IChatMessage } from '@nimbus-global/features/chat/models/chat.model';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { ChatMediaPolicy, ChatSendMessageRequest, ChatSendMessageResponse, ChatServiceBase } from '../../models/chat-service.model';
import { ToChatMessageMapper } from '../../models/chat-service.mapper';
import {
  IChatDraftMessage,
  getUpdatedTwilioStatus,
  LastMessageIndexRequest,
  TwilioLiveMessage,
  TwilioMessage,
  ITwilioMessageResponse,
  TwilioMessageStatus,
  TwilioMessageStatusUpdate,
} from '../../models/chat-twilio.model';
import { ChatDraftService } from '../chat-draft.service';

@Injectable()
export class ChatTwilioService implements ChatServiceBase {
  private chatMessages$: BehaviorSubject<IChatMessage[]> = new BehaviorSubject<IChatMessage[]>([]);
  private isInitialLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private loadingMediaPolicy$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private mediaPolicySubject$: BehaviorSubject<ChatMediaPolicy | null> = new BehaviorSubject<ChatMediaPolicy | null>(null);
  private destroy$ = new Subject<void>();

  public readonly messages$ = this.chatMessages$.asObservable();
  public readonly connectionState$ = this.signalRHub.isConnected$.asObservable();
  public readonly mediaPolicy$ = this.mediaPolicySubject$.asObservable();
  public readonly loading$ = combineLatest([this.isInitialLoading$, this.loadingMediaPolicy$]).pipe(
    map(([messagesLoading, mediaPolicyLoading]) => messagesLoading || mediaPolicyLoading),
  );

  public constructor(
    private readonly apiClient: ChatTwilioApiClient,
    private readonly signalRHub: NimbusSignalRHub,
    private readonly chatMessageMapper: ToChatMessageMapper,
    private readonly chatDraftService: ChatDraftService,
    private readonly featureFlippingService: FeatureFlippingService,
  ) {}

  public initialize(apiUrl: string, callerInfo: IChatCallerInfo, conversationId: string): void {
    this.signalRHub.onChatHandlerMessageAdded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => this.onMessageAdded(message, callerInfo, conversationId));
    this.signalRHub.onChatHandlerMessageStatusUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statusUpdate) => this.onMessageStatusUpdate(statusUpdate, conversationId));
    this.receiveMessages(apiUrl, callerInfo);
  }

  public loadMediaPolicy(tenantId: string, mediaPolicyApiUrl: string): void {
    this.featureFlippingService
      .isEnabledInFeatureManagementByTenantId$(tenantId, Feature.ChatHandlerMediaHandling)
      .pipe(
        take(1),
        switchMap((isEnabled) => (isEnabled === true ? this.apiClient.getMediaPolicy(mediaPolicyApiUrl) : of(null))),
        catchError(() => of(null)),
        finalize(() => this.loadingMediaPolicy$.next(false)),
        takeUntil(this.destroy$),
      )
      .subscribe((mediaPolicy) => this.mediaPolicySubject$.next(mediaPolicy));
  }

  public sendMessage(apiUrl: string, request: ChatSendMessageRequest): Observable<ChatSendMessageResponse> {
    return this.apiClient.sendMessage(apiUrl, request);
  }

  public sendMedia(apiUrl: string, file: File): Observable<ChatSendMessageResponse> {
    return this.apiClient.sendMedia(apiUrl, file);
  }

  public reset(): void {
    this.destroy$.next();
    this.chatMessages$.next([]);
    this.isInitialLoading$.next(true);
    this.mediaPolicySubject$.next(null);
    this.loadingMediaPolicy$.next(true);
  }

  public destroy(): void {
    this.reset();
    this.destroy$.complete();
    this.chatMessages$.complete();
    this.isInitialLoading$.complete();
    this.mediaPolicySubject$.complete();
    this.loadingMediaPolicy$.complete();
  }

  public receiveMessages(apiUrl: string, callerInfo: IChatCallerInfo): void {
    this.receiveHistoricalMessagesWithRetry(apiUrl)
      .pipe(
        map((response: ITwilioMessageResponse) => ({
          chat: this.chatMessageMapper.twilioToChatMessagesMapper(response.messages, callerInfo),
          draft: response.draft,
        })),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ chat, draft }: { chat: IChat; draft: IChatDraftMessage }) => {
          this.chatMessages$.next(chat.messages);
          this.chatDraftService.setCurrentDraftText(draft?.body ?? '');
          if (this.isInitialLoading$.value) {
            this.isInitialLoading$.next(false);
          }
        },
        error: () => {
          this.isInitialLoading$.next(false);
        },
      });
  }

  public markChatMessagesAsRead(apiUrl: string, request: LastMessageIndexRequest): Observable<void> {
    return this.apiClient.markMessagesAsRead(apiUrl, request);
  }

  private receiveHistoricalMessagesWithRetry(apiUrl: string): Observable<ITwilioMessageResponse> {
    const maxRetries = 3;
    const baseDelayMs = 1000;
    const exponentialFactor = 2;
    const jitterRatio = 0.2;

    return this.apiClient.receiveMessages(apiUrl).pipe(
      map((response) => ({
        messages: response?.messages?.filter((x) => x.deliveryState !== TwilioMessageStatus.Failed) ?? [],
        draft: response?.draft,
      })),
      retry({
        count: maxRetries,
        delay: (_error, attempt) => {
          const base = baseDelayMs * Math.pow(exponentialFactor, attempt - 1);
          const jitter = Math.round(base * jitterRatio * Math.random());
          return timer(base + jitter);
        },
      }),
      catchError((_error) => {
        const systemMessage: TwilioMessage = {
          messageSid: `system_get_error_${Date.now()}`,
          author: 'System',
          body: 'GET Messages are currently not working',
          deliveryState: TwilioMessageStatus.None,
          index: -1,
          dateCreated: new Date().toISOString(),
        };
        return of({ messages: [systemMessage], draft: { body: '' } });
      }),
    );
  }

  private onMessageAdded(message: TwilioLiveMessage, callerInfo: IChatCallerInfo, conversationId: string): void {
    if (message.conversationId === conversationId) {
      const currentMessages = this.chatMessages$.value;
      if (!currentMessages.find((msg) => msg.id === message.messageSid)) {
        const authorType = this.chatMessageMapper.getAuthorType(message.author, callerInfo.id);
        const updatedMessages = [
          ...currentMessages,
          this.chatMessageMapper.mapTwilioMessageToChatMessage(
            message,
            callerInfo,
            currentMessages.length,
            currentMessages[currentMessages.length - 1]?.dateTime,
            authorType === AuthorType.Agent ? TwilioMessageStatus.Pending : null,
          ),
        ];
        this.chatMessages$.next(updatedMessages.sort((a, b) => a.index - b.index));
      }
    }
  }

  private onMessageStatusUpdate(messageUpdated: TwilioMessageStatusUpdate, conversationId: string): void {
    if (messageUpdated.conversationId === conversationId) {
      const currentMessages = this.chatMessages$.value;
      const messageToUpdate = currentMessages.find((msg) => msg.id === messageUpdated.messageSid);
      if (messageToUpdate) {
        messageUpdated.status = messageUpdated.status ?? TwilioMessageStatus.None;
        const updatedMessages = currentMessages.map((msg) =>
          msg.id === messageUpdated.messageSid
            ? {
                ...msg,
                status: getUpdatedTwilioStatus(messageToUpdate.status ?? TwilioMessageStatus.None, messageUpdated.status),
                failureReason: messageUpdated.failureReason ?? msg.failureReason,
              }
            : msg,
        );
        this.chatMessages$.next(updatedMessages.sort((a, b) => a.index - b.index));
      }
    }
  }
}
